import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  Tab,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  Code,
  Divider,
  Button,
  useColorModeValue,
  VStack
} from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import AleaButtonChoice from '../../components/AleaButtonChoice/AleaButtonChoice';
import PageHeader from '../../components/PageHeader/PageHeader';

const AleaButtonChoiceGuide: React.FC = () => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // State for interactive examples
  const [selectedValue1, setSelectedValue1] = useState('Option 1');
  const [selectedValue2, setSelectedValue2] = useState('Male');
  const [selectedValue3, setSelectedValue3] = useState('Small');
  const [selectedValue4, setSelectedValue4] = useState('Enabled');

  // Example data
  const basicOptions = ['Option 1', 'Option 2', 'Option 3'];
  const genderOptions = ['Male', 'Female', 'Other'];
  const sizeOptions = ['Small', 'Medium', 'Large'];
  const statusOptions = ['Enabled', 'Disabled'];

  return (
    <Flex flexFlow="column">
      <PageHeader title={"Button Choice"} />
      <Box p={8} bg="#fff" minH="100vh" borderRadius="20px">
        <Box maxW="1200px" mx="auto">
          <Flex mb={8} align="center">
            <Button
              leftIcon={<ChevronLeftIcon />}
              variant="ghost"
              onClick={() => navigate('/style-guide')}
              mr={4}
            >
              Back to Style Guide
            </Button>
          </Flex>

          <Text mb={6}>
            AleaButtonChoice is a reusable component that provides a group of selectable buttons.
            It's commonly used for single-choice selections where users can visually see all available options.
          </Text>

          <Tabs variant="enclosed" mb={8}>
            <TabList>
              <Tab>Examples</Tab>
              <Tab>Props</Tab>
              <Tab>Usage</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <Box mb={8}>
                  <Heading size="md" mb={4}>Basic Usage</Heading>
                  <Text mb={4}>
                    Simple button choice with three options. The currently selected option is highlighted.
                  </Text>
                  <Box bg={bgColor} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor} mb={4}>
                    <VStack align="start" spacing={4}>
                      <Text fontSize="sm" color="gray.600">Selected: {selectedValue1}</Text>
                      <AleaButtonChoice
                        label="Choose an option"
                        items={basicOptions}
                        value={selectedValue1}
                        onChange={setSelectedValue1}
                      />
                    </VStack>
                  </Box>
                </Box>

                <Divider my={6} />

                <Box mb={8}>
                  <Heading size="md" mb={4}>Full Width</Heading>
                  <Text mb={4}>
                    Button choice that spans the full width of its container, with responsive wrapping.
                  </Text>
                  <Box bg={bgColor} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor} mb={4}>
                    <VStack align="start" spacing={4}>
                      <Text fontSize="sm" color="gray.600">Selected: {selectedValue2}</Text>
                      <AleaButtonChoice
                        label="Gender"
                        items={genderOptions}
                        value={selectedValue2}
                        onChange={setSelectedValue2}
                        fullWidth
                      />
                    </VStack>
                  </Box>
                </Box>

                <Divider my={6} />

                <Box mb={8}>
                  <Heading size="md" mb={4}>With Error State</Heading>
                  <Text mb={4}>
                    Button choice showing error state with validation message.
                  </Text>
                  <Box bg={bgColor} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor} mb={4}>
                    <VStack align="start" spacing={4}>
                      <Text fontSize="sm" color="gray.600">Selected: {selectedValue3}</Text>
                      <AleaButtonChoice
                        label="Size"
                        items={sizeOptions}
                        value={selectedValue3}
                        onChange={setSelectedValue3}
                        errorMessage="Please select a size"
                      />
                    </VStack>
                  </Box>
                </Box>

                <Divider my={6} />

                <Box mb={8}>
                  <Heading size="md" mb={4}>Disabled State</Heading>
                  <Text mb={4}>
                    Button choice in disabled state where no interaction is allowed.
                  </Text>
                  <Box bg={bgColor} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor} mb={4}>
                    <VStack align="start" spacing={4}>
                      <Text fontSize="sm" color="gray.600">Selected: {selectedValue4}</Text>
                      <AleaButtonChoice
                        label="Status"
                        items={statusOptions}
                        value={selectedValue4}
                        onChange={setSelectedValue4}
                        disabled
                      />
                    </VStack>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel>
                <Box mb={8}>
                  <Heading size="md" mb={4}>Props</Heading>
                  <Box overflowX="auto">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Prop</th>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Type</th>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Default</th>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>label</Code></td>
                          <td style={{ padding: '12px' }}><Code>string</Code></td>
                          <td style={{ padding: '12px' }}><Code>-</Code></td>
                          <td style={{ padding: '12px' }}>Label text displayed above the button group</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>items</Code></td>
                          <td style={{ padding: '12px' }}><Code>string[]</Code></td>
                          <td style={{ padding: '12px' }}><Code>[]</Code></td>
                          <td style={{ padding: '12px' }}>Array of option strings to display as buttons</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>value</Code></td>
                          <td style={{ padding: '12px' }}><Code>string</Code></td>
                          <td style={{ padding: '12px' }}><Code>-</Code></td>
                          <td style={{ padding: '12px' }}>Currently selected value from the items array</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>onChange</Code></td>
                          <td style={{ padding: '12px' }}><Code>{'(value: string) => void'}</Code></td>
                          <td style={{ padding: '12px' }}><Code>-</Code></td>
                          <td style={{ padding: '12px' }}>Function called when a button is clicked</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>errorMessage</Code></td>
                          <td style={{ padding: '12px' }}><Code>string</Code></td>
                          <td style={{ padding: '12px' }}><Code>undefined</Code></td>
                          <td style={{ padding: '12px' }}>Error message to display when validation fails</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>disabled</Code></td>
                          <td style={{ padding: '12px' }}><Code>boolean</Code></td>
                          <td style={{ padding: '12px' }}><Code>false</Code></td>
                          <td style={{ padding: '12px' }}>Whether the entire button group is disabled</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>fullWidth</Code></td>
                          <td style={{ padding: '12px' }}><Code>boolean</Code></td>
                          <td style={{ padding: '12px' }}><Code>false</Code></td>
                          <td style={{ padding: '12px' }}>Whether buttons should take full width of container</td>
                        </tr>
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel>
                <Box mb={8}>
                  <Heading size="md" mb={4}>Usage</Heading>
                  <Code p={4} borderRadius="md" w="100%" display="block" whiteSpace="pre">
  {`// Step 1: Import the component
  import AleaButtonChoice from '../../components/AleaButtonChoice/AleaButtonChoice';
  import { useState } from 'react';

  // Step 2: Set up state
  const [selectedOption, setSelectedOption] = useState('option1');

  // Step 3: Define options
  const options = ['Option 1', 'Option 2', 'Option 3'];

  // Step 4: Handle changes
  const handleOptionChange = (value: string) => {
    setSelectedOption(value);
    console.log('Selected:', value);
  };

  // Step 5: Use in JSX
  <AleaButtonChoice
    label="Choose your preference"
    items={options}
    value={selectedOption}
    onChange={handleOptionChange}
  />`}
                  </Code>
                </Box>

                <Box mb={8}>
                  <Heading size="md" mb={4}>With Validation</Heading>
                  <Code p={4} borderRadius="md" w="100%" display="block" whiteSpace="pre">
  {`// Example with error handling
  const [selectedSize, setSelectedSize] = useState('');
  const [error, setError] = useState('');

  const handleSizeChange = (value: string) => {
    setSelectedSize(value);
    setError(''); // Clear error on change
  };

  const handleSubmit = () => {
    if (!selectedSize) {
      setError('Please select a size');
    } else {
      // Process form
    }
  };

  <AleaButtonChoice
    label="Select Size"
    items={['Small', 'Medium', 'Large']}
    value={selectedSize}
    onChange={handleSizeChange}
    errorMessage={error}
  />`}
                  </Code>
                </Box>

                <Box mb={8}>
                  <Heading size="md" mb={4}>Full Width Usage</Heading>
                  <Code p={4} borderRadius="md" w="100%" display="block" whiteSpace="pre">
  {`// Full width for responsive layouts
  <AleaButtonChoice
    label="Choose your plan"
    items={['Basic', 'Pro', 'Enterprise']}
    value={selectedPlan}
    onChange={setSelectedPlan}
    fullWidth
  />`}
                  </Code>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Box>
    </Flex>
  );
};

export default AleaButtonChoiceGuide;
